import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Collapsible from 'react-native-collapsible';
import { Divider, IconButton } from 'react-native-paper';

import { DownIcon, ErrorIcon } from '@/assets';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { SectionContent } from '@/features/start/components/components/SectionContent';
import { CivilProtectionWarnings } from '@/features/start/components/header/civil-protection/CivilProtectionWarnings';
import { StartRoute } from '@/features/start/navigation/startRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { RotatableView } from '@/shared/components/animated/RotatableView';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const styles = StyleSheet.create({
  footer: { alignItems: 'center', maxHeight: 32 }
});

type Props = {
  items: GenericPostDetail[];
};

export const KatwarnCard: FC<Props> = ({ items }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'components.civilProtection'
  });

  const [expanded, setExpanded] = useState(false);
  const handleExpandChange = useCallback(
    () => setExpanded((prevValue) => !prevValue),
    []
  );

  const firstTwo = items.slice(0, 2);
  const rest = items.slice(2);

  return (
    <>
      <SectionContent
        title={t('title', { count: items.length })}
        testId="card-civil-protection"
        screen={StartRoute.CivilProtection}
        params={{ count: items.length }}
        titleVariant="headlineSmall"
        icon={ErrorIcon}
        titleColor={theme.colors.error}
      />

      <CivilProtectionWarnings items={firstTwo} />

      <Collapsible collapsed={!expanded} renderChildrenCollapsed>
        <Divider />
        <CivilProtectionWarnings items={rest} />
      </Collapsible>

      {rest.length > 0 && (
        <View style={styles.footer}>
          <IconButton
            icon={({ color }) => (
              <RotatableView changeRotation={expanded}>
                <DownIcon width={32} height={32} fill={color} />
              </RotatableView>
            )}
            onPress={handleExpandChange}
          />
        </View>
      )}
    </>
  );
};

KatwarnCard.displayName = 'KatwarnCard';
